<template>
  <transition
    mode="out-in"
    @before-leave="beforeLeave"
    @before-enter="beforeEnter"
    @before-appear="beforeAppear"
    @appear="appear"
    @enter="enter"
    @leave="leave"
    @after-appear="afterAppear"
    @after-enter="afterEnter">
    <slot>
    </slot>
  </transition>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import anime from 'animejs';
import { SplitText } from '@/scripts/SplitText';

export default {
  name: 'StaggeringTransition',
  props: {
    delay: {
      type: Number,
      default: 0,
    },
    isOver: {
      type: Boolean,
      default: false,
    },
    emitter: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState([
      'pageInTransition',
      'transitionDuration',
    ]),
  },
  methods: {
    ...mapMutations([
      'toggleArrowIsAnimating',
    ]),

    // ANIMATION
    animation(targets, done, reverse = false) {
      return anime({
        targets,
        autoplay: false,
        opacity: {
          value: reverse ? 0 : [0, 1],
          duration: 1000,
          delay: reverse ? 500 + this.delay : this.delay,
          easing: () => (!reverse ? 'easeInCubic' : 'easeOutCubic'),
        },
        translateY: {
          value: () => (
            reverse
              ? [0, '-90%']
              : ['90%', 0]
          ),
          duration: () => anime.random(1100, 1200),
          delay: () => anime.random(0, 400) + this.delay,
          easing: () => (!reverse ? 'easeOutCubic' : 'easeInCubic'),
        },
        complete: () => (done ? done() : null),
      });
    },

    // BEFORE
    beforeLeave(el) {
      anime.remove(el);
      if (this.emitter) this.$store.commit(this.emitter, true);
      this.$emit('staggering', true);
    },

    beforeEnter(el) {
      anime.remove(el);

      if (el.nodeName !== 'svg' && el.nodeName !== 'img') {
        const split = new SplitText(el, { charsClass: 'stagger-element' });
        anime.set(split.chars, { translateZ: 0 });
      }
    },

    beforeAppear(el) {
      if (this.emitter) this.$store.commit(this.emitter, true);

      this.$emit('staggering', true);
      if (el.nodeName !== 'svg' && el.nodeName !== 'img') {
        const split = new SplitText(el, { charsClass: 'stagger-element' });
        anime.set(split.chars, { translateZ: 0 });
      }
    },

    // MAIN
    appear(el, done) {
      const targets = el.getElementsByClassName('stagger-element');

      const animation = this.animation(targets, done, false);
      animation.play();
    },

    leave(el, done) {
      this.$nextTick(() => {
        let animation;
        const targets = el.getElementsByClassName('stagger-element');

        if (targets.length > 0) animation = this.animation(targets, done, true);
        else animation = this.animation(el, done, true);

        animation.play();
      });
    },

    enter(el, done) {
      let animation;
      const targets = el.getElementsByClassName('stagger-element');

      if (targets.length > 0) {
        animation = this.animation(targets, done, false, this.isOver);
      } else {
        animation = this.animation(el, done, false, this.isOver);
      }

      if (this.pageInTransition) {
        setTimeout(() => animation.play(), 1000);
      } else {
        animation.play();
      }
    },

    // AFTER
    afterAppear() {
      if (this.emitter) this.$store.commit(this.emitter, false);
      this.$emit('staggering', false);
    },

    afterEnter() {
      if (this.emitter) this.$store.commit(this.emitter, false);
      this.$emit('staggering', false);
    },
  },
};
</script>
